import { Box, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import type { Moment } from "moment";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { PortalLifecycleStatus, type PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { formatTimeLeft, type TimeLeft, timeLeftUntil } from "../../../../common/formatting/timeLeft";
import { formatDeadline, formatShortDate } from "../utils/displayFormatters";
import {
  continuousAdmissionUntil,
  deadlineLabelKey,
  effectiveDeadlineIso,
  qualificationSchemeExpiry,
} from "../utils/effectiveDeadline";
import { useCurrentMoment } from "../utils/useCurrentMoment";

export const EventDeadlineHighlight = ({
  event,
  now,
}: {
  event: PortalSourcingEventDetailDto;
  now?: Moment;
}) => {
  const { t, i18n } = useTranslation();
  const locale = momentLocaleFor(i18n.language);
  // The page passes its own clock so the card and the countdown badge never drift apart; standalone
  // callers (the bid wizard) get their own ticking one.
  const ownClock = useCurrentMoment();
  const deadlineIso = effectiveDeadlineIso(event);
  const schemeExpiry = qualificationSchemeExpiry(event, deadlineIso);

  // The live countdown only makes sense while the competition is open; other states are conveyed by the lifecycle banner.
  if (event.lifecycle.status !== PortalLifecycleStatus.Open) return null;
  if (event.frameworkAgreementNoLongerActive) return null;
  if (schemeExpiry == null && deadlineIso == null) return null;

  const admissionUntil = continuousAdmissionUntil(event, deadlineIso, now ?? ownClock);
  const label = schemeExpiry
    ? t("The system expires")
    : admissionUntil
      ? t("Continuous admission")
      : t(deadlineLabelKey(event));
  const date = schemeExpiry
    ? formatShortDate(schemeExpiry, locale)
    : admissionUntil
      ? t("Applications are accepted until {{date}}", { date: formatShortDate(admissionUntil, locale) })
      : deadlineIso && formatDeadline(deadlineIso, t("at"), locale);
  const timeLeft = schemeExpiry == null && admissionUntil == null && deadlineIso ? timeLeftUntil(deadlineIso, now ?? ownClock) : null;

  return <DeadlineHighlightCard label={label} date={date ?? ""} timeLeft={timeLeft} />;
};

export const DeadlineHighlightCard = ({
  label,
  date,
  timeLeft,
}: {
  label: string;
  date: string;
  timeLeft: TimeLeft | null;
}) => {
  const { t } = useTranslation();
  return (
    <Card variant="outline" rounded="xl" bg="smPrimarySofter" borderColor="smPrimarySoft">
      <CardBody px={{ base: "4", md: "6" }} py={{ base: "5", md: "5" }}>
        <Flex align="center" justify="space-between" gap="4">
          <Flex align="center" gap="4">
            <Flex
              width={{ base: "44px", md: "52px" }}
              height={{ base: "44px", md: "52px" }}
              rounded="full"
              bg="smBackground"
              align="center"
              justify="center"
              flexShrink="0"
            >
              <Icon as={FiClock} boxSize="6" color="smPrimary" />
            </Flex>
            <Stack spacing="0">
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="smPrimary"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {label}
              </Text>
              <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color="smText">
                {date}
              </Text>
            </Stack>
          </Flex>

          {timeLeft && (
            <Box textAlign="right">
              {timeLeft.unit === "daysAndHours" ? (
                <>
                  <Text fontSize={{ base: "4xl", md: "5xl" }} fontWeight="bold" color="smPrimary" lineHeight="1">
                    {timeLeft.days}
                  </Text>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="smPrimary"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mt="1"
                  >
                    {t("days_left", { count: timeLeft.days })}
                  </Text>
                  {timeLeft.hours > 0 && (
                    <Text fontSize="xs" color="smMuted">
                      {t("+ {{hours}} h", { hours: timeLeft.hours })}
                    </Text>
                  )}
                </>
              ) : (
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="smPrimary"
                  lineHeight="1.2"
                >
                  {formatTimeLeft(timeLeft, t)}
                </Text>
              )}
            </Box>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
};
